import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { logoutSession } from '@/api/auth.api';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useCurrentShift } from '@/hooks/queries';
import { isFocOnly } from '@/lib/permissions';
import { useAuthStore } from '@/stores/auth.store';

/**
 * "Keluar" for the POS. With a shift still open it asks first: close the shift
 * (the drawer count on `/shift/close`) or just sign out — the shift stays open
 * and the cashier carries on with it at the next login. With no shift open it
 * signs out straight away. An FOC-only account has no shift and is never asked.
 *
 * Returns `requestLogout` for the button and the `dialog` to render beside it.
 */
export function useShiftAwareLogout(onBeforeLeave?: () => void) {
  const navigate = useNavigate();
  const logout = useAuthStore((s) => s.logout);
  const focOnly = useAuthStore((s) => isFocOnly({ permissions: s.permissions, wildcards: s.wildcards }));
  const { data: currentShift } = useCurrentShift(!focOnly);
  const [asking, setAsking] = useState(false);

  const signOut = async () => {
    setAsking(false);
    onBeforeLeave?.();
    // Revoke on the server FIRST, while the token is still in the store — the
    // axios request interceptor reads it from there, so clearing local state
    // first sends the revoke unauthenticated and the session survives.
    await logoutSession(useAuthStore.getState().refreshToken);
    logout();
    navigate('/login', { replace: true });
  };

  const requestLogout = () => {
    if (currentShift) {
      onBeforeLeave?.();
      setAsking(true);
      return;
    }
    void signOut();
  };

  const dialog = (
    <AlertDialog open={asking} onOpenChange={setAsking}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Shift masih berjalan</AlertDialogTitle>
          <AlertDialogDescription>
            Tutup shift dulu (hitung laci kas) sebelum keluar, atau keluar saja dan lanjutkan shift ini saat
            masuk lagi.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Batal</AlertDialogCancel>
          <Button variant="outline" onClick={() => void signOut()}>
            Keluar saja
          </Button>
          <Button
            onClick={() => {
              setAsking(false);
              navigate('/shift/close');
            }}
          >
            Tutup shift
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { requestLogout, dialog };
}
